/**
 * Reusable mystical skeleton loaders.
 * All shimmer with the gold/sand palette to match the app's aesthetic.
 */

const Shimmer = ({ className = '', style = {} }) => (
  <div
    className={`skeleton-shimmer ${className}`}
    style={{
      background: 'linear-gradient(110deg, rgba(198,178,143,0.10) 25%, rgba(198,178,143,0.22) 50%, rgba(198,178,143,0.10) 75%)',
      backgroundSize: '200% 100%',
      animation: 'skeleton-pulse 1.6s ease-in-out infinite',
      borderRadius: 12,
      ...style,
    }}
  />
);

export function DashboardSkeleton() {
  return (
    <div className="space-y-8" data-testid="dashboard-skeleton">
      <div className="space-y-3">
        <Shimmer style={{ height: 14, width: 140 }} />
        <Shimmer style={{ height: 48, width: '60%' }} />
        <Shimmer style={{ height: 18, width: '40%' }} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
        <Shimmer className="md:col-span-5 lg:col-span-7" style={{ height: 220 }} />
        <div className="md:col-span-3 lg:col-span-5 space-y-4">
          <Shimmer style={{ height: 80 }} />
          <Shimmer style={{ height: 80 }} />
          <Shimmer style={{ height: 80 }} />
        </div>
        <Shimmer className="md:col-span-4 lg:col-span-4" style={{ height: 140 }} />
        <Shimmer className="md:col-span-4 lg:col-span-4" style={{ height: 140 }} />
        <Shimmer className="md:col-span-4 lg:col-span-4" style={{ height: 140 }} />
      </div>
    </div>
  );
}

export function JournalSkeleton() {
  return (
    <div className="space-y-8" data-testid="journal-skeleton">
      <div className="space-y-3">
        <Shimmer style={{ height: 14, width: 120 }} />
        <Shimmer style={{ height: 44, width: '40%' }} />
      </div>
      <Shimmer style={{ height: 180 }} />
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <Shimmer key={i} style={{ height: 120 }} />
        ))}
      </div>
    </div>
  );
}

export function MeditationsSkeleton() {
  return (
    <div className="space-y-8" data-testid="meditations-skeleton">
      <div className="space-y-3">
        <Shimmer style={{ height: 14, width: 160 }} />
        <Shimmer style={{ height: 44, width: '35%' }} />
        <Shimmer style={{ height: 18, width: '50%' }} />
      </div>
      <div className="flex gap-2 flex-wrap">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Shimmer key={i} style={{ height: 32, width: 110, borderRadius: 999 }} />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rounded-2xl overflow-hidden">
            <Shimmer style={{ height: 192, borderRadius: 0 }} />
            <div className="p-6 space-y-3">
              <Shimmer style={{ height: 24, width: '70%' }} />
              <Shimmer style={{ height: 14, width: '100%' }} />
              <Shimmer style={{ height: 14, width: '85%' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EchoMapSkeleton() {
  return (
    <div className="space-y-10" data-testid="echo-map-skeleton">
      <div className="space-y-3">
        <Shimmer style={{ height: 14, width: 130 }} />
        <Shimmer style={{ height: 44, width: '40%' }} />
        <Shimmer style={{ height: 18, width: '60%' }} />
      </div>
      <Shimmer style={{ height: 140 }} />
      <div className="grid grid-cols-3 gap-3">
        <Shimmer style={{ height: 100 }} />
        <Shimmer style={{ height: 100 }} />
        <Shimmer style={{ height: 100 }} />
      </div>
      <Shimmer style={{ height: 420 }} />
    </div>
  );
}

export function ChatListSkeleton() {
  return (
    <div className="space-y-2 p-4" data-testid="chat-list-skeleton">
      {[0, 1, 2, 3, 4].map((i) => (
        <Shimmer key={i} style={{ height: 64 }} />
      ))}
    </div>
  );
}

export function MessageSkeleton() {
  return (
    <div className="space-y-6" data-testid="message-skeleton">
      <Shimmer style={{ height: 14, width: '20%' }} />
      <Shimmer style={{ height: 48 }} />
      <Shimmer style={{ height: 14, width: '20%', marginLeft: 'auto' }} />
      <Shimmer style={{ height: 64 }} />
    </div>
  );
}
